// src/components/collections/CityHighlightsRow.tsx
//
// Renders the personalized must-see list at the top of the Guide
// page as a horizontal row of circle tiles. Each tile = one
// recommended item; checkmark badge if discovered (photo OR
// proximity); rationale visible on tap.
//
// States:
//   - Loading (city known, generating): skeleton row
//   - Empty (no city yet): renders nothing
//   - Cold-start basis: header copy shifts from "Picked for you"
//     to "Where to start", since we haven't personalized yet
//
// Discovery tap fallback: if a user knows they've been somewhere
// but the photo/proximity gates didn't fire, they can tap "I've
// been here" inside the detail sheet — writes
// `discovered_via: 'self_tap'` onto the item array via the
// rules-allowed merge.

import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Drawer,
  IconButton,
  Button,
  Chip,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Place,
  AutoAwesome,
  PhotoCamera,
  TaskAlt,
  Directions,
} from '@mui/icons-material';
import { useCityHighlights } from '../../hooks/useCityHighlights';
import { CityHighlightItem } from '../../types/collections';
import { useResolvedImageUrl } from '../../feed/useResolvedImageUrl';
import { auth, db } from '../../services/firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { useT } from '../../i18n/useT';
import { NavigateToCard } from '../navigate/NavigateToCard';
import type { NavigateTarget } from '../../utils/interactiveContent';

interface Props {
  /** Optional callback when the user taps "ask about this place"
   *  in the detail sheet. Wired by GuidePage to push a chat seed. */
  onAskAbout?: (item: CityHighlightItem) => void;
}

const TILE_SIZE = 76; // px diameter of each circle

const CategoryBadge: React.FC<{ category: CityHighlightItem['category'] }> = ({
  category,
}) => {
  const theme = useTheme();
  const map = {
    icon: { color: theme.palette.primary.main, label: 'Icon' },
    personal: { color: theme.palette.secondary.main, label: 'For you' },
    hidden_gem: { color: '#d97706', label: 'Hidden gem' },
  } as const;
  const meta = map[category];
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        height: 20,
        fontSize: '0.7rem',
        bgcolor: meta.color,
        color: 'white',
        fontWeight: 600,
      }}
    />
  );
};

const HighlightTile: React.FC<{
  item: CityHighlightItem;
  onClick: () => void;
}> = ({ item, onClick }) => {
  const cover = useResolvedImageUrl(
    item.cover_image_thumb || item.cover_image_url || undefined
  );
  const discovered = !!item.discovered_at;
  const t = useT();

  return (
    <Box
      onClick={onClick}
      role="button"
      aria-label={t('Open {title}', { title: item.title })}
      sx={{
        flexShrink: 0,
        width: 92,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.75,
        // Subtle tap feedback
        transition: 'transform 120ms ease-out',
        '&:active': { transform: 'scale(0.96)' },
      }}
    >
      <Box
        sx={{
          width: TILE_SIZE,
          height: TILE_SIZE,
          borderRadius: '50%',
          overflow: 'hidden',
          position: 'relative',
          backgroundImage: cover ? `url(${cover})` : undefined,
          backgroundColor: cover ? undefined : 'grey.300',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          // Discovered items get a green ring + slightly faded
          // tile, communicating "done" without burying it
          border: discovered
            ? '3px solid'
            : '2px solid',
          borderColor: discovered ? 'success.main' : 'divider',
          opacity: discovered ? 0.78 : 1,
        }}
      >
        {!cover && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <Place fontSize="medium" />
          </Box>
        )}
        {/* Checkmark overlay */}
        {discovered && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'success.main',
              border: '2px solid',
              borderColor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
          </Box>
        )}
      </Box>
      <Typography
        variant="caption"
        align="center"
        sx={{
          width: '100%',
          lineHeight: 1.2,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontWeight: discovered ? 400 : 500,
          color: discovered ? 'text.secondary' : 'text.primary',
        }}
      >
        {item.title}
      </Typography>
    </Box>
  );
};

const HighlightDetailSheet: React.FC<{
  item: CityHighlightItem | null;
  cityKey: string | null;
  onClose: () => void;
  onAskAbout?: (item: CityHighlightItem) => void;
}> = ({ item, cityKey, onClose, onAskAbout }) => {
  const t = useT();
  const cover = useResolvedImageUrl(
    item?.cover_image_url || item?.cover_image_thumb || undefined
  );
  const [marking, setMarking] = useState(false);
  const [navigateOpen, setNavigateOpen] = useState(false);

  if (!item) return null;
  const discovered = !!item.discovered_at;
  // Walking directions only when we have real coords. The server
  // populates `item.location` only when it could resolve them, so
  // the absence of coords means "we couldn't find this on the map" —
  // surfacing a broken-button is worse than hiding it.
  const hasCoords =
    !!item.location &&
    typeof item.location.latitude === 'number' &&
    typeof item.location.longitude === 'number' &&
    !(item.location.latitude === 0 && item.location.longitude === 0);
  const navigateTarget: NavigateTarget | null = hasCoords
    ? {
        name: item.title,
        lat: item.location!.latitude,
        lng: item.location!.longitude,
        reasoning: item.rationale || '',
      }
    : null;

  const handleSelfTap = async () => {
    const user = auth.currentUser;
    if (!user || !cityKey) return;
    setMarking(true);
    try {
      // Read-modify-write the items array. The rules allow update
      // to the `items` field only. We can't do a server-side
      // arrayUpdate by index, so we round-trip the whole array.
      const ref = doc(db, 'users', user.uid, 'city_highlights', cityKey);
      const { getDoc } = await import('firebase/firestore');
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const d = snap.data() as any;
      const next = (Array.isArray(d.items) ? d.items : []).map((it: any) => {
        if (it.id !== item.id) return it;
        return {
          ...it,
          discovered_at: Timestamp.now(),
          discovered_via: 'self_tap',
        };
      });
      await updateDoc(ref, { items: next });
    } catch (err) {
      console.warn('self-tap mark failed:', err);
    } finally {
      setMarking(false);
      onClose();
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={!!item}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '85dvh',
          pb: 'env(safe-area-inset-bottom, 0px)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {cover && (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              backgroundImage: `url(${cover})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: 'grey.300',
            }}
          />
        )}
        <IconButton
          onClick={onClose}
          aria-label={t('Close')}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CategoryBadge category={item.category} />
          {discovered && (
            <Chip
              size="small"
              icon={<CheckCircle sx={{ fontSize: 14 }} />}
              label={t('Found')}
              color="success"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>

        <Typography variant="h6" sx={{ mb: 1 }}>
          {item.title}
        </Typography>

        {item.rationale && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 2,
              bgcolor: 'action.hover',
              p: 1.5,
              borderRadius: 2,
            }}
          >
            <AutoAwesome
              sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0, mt: 0.25 }}
            />
            <Typography variant="body2" color="text.secondary">
              {item.rationale}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {onAskAbout && (
            <Button
              variant="contained"
              startIcon={<AutoAwesome />}
              fullWidth
              onClick={() => {
                onAskAbout(item);
                onClose();
              }}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {t('Tell me about {title}', { title: item.title })}
            </Button>
          )}
          {navigateTarget && (
            <Button
              variant="outlined"
              startIcon={<Directions />}
              fullWidth
              onClick={() => setNavigateOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {t('Walking directions')}
            </Button>
          )}
          {!discovered && (
            <Button
              variant="outlined"
              startIcon={<TaskAlt />}
              fullWidth
              disabled={marking}
              onClick={handleSelfTap}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {marking ? t("Marking…") : t("I've been here")}
            </Button>
          )}
          {!discovered && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
            >
              <PhotoCamera sx={{ fontSize: 14 }} />
              {t('Or take a photo nearby and the checkmark appears automatically')}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Walking-directions sheet — controlled, no inline teaser.
          Mounts only when we have coords, so a missing-location
          item silently has no button rather than a broken one. */}
      {navigateTarget && (
        <NavigateToCard
          target={navigateTarget}
          open={navigateOpen}
          onClose={() => setNavigateOpen(false)}
          hideTeaser
        />
      )}
    </Drawer>
  );
};

export const CityHighlightsRow: React.FC<Props> = ({ onAskAbout }) => {
  const t = useT();
  const { highlights, city, totalCount, discoveredCount } =
    useCityHighlights();
  const [selected, setSelected] = useState<CityHighlightItem | null>(null);

  // Sort: undiscovered first (still to-do), then discovered, preserving
  // order_hint within each group. Discovered fade to the right so the
  // user's eye lands on what's left to find.
  const items = useMemo(() => {
    if (!highlights) return [];
    const undisc = highlights.items
      .filter((i) => !i.discovered_at)
      .sort((a, b) => a.order_hint - b.order_hint);
    const disc = highlights.items
      .filter((i) => !!i.discovered_at)
      .sort((a, b) => a.order_hint - b.order_hint);
    return [...undisc, ...disc];
  }, [highlights]);

  // Header copy
  const heading = useMemo(() => {
    if (!highlights || highlights.basis === 'cold_start') {
      return t('Where to start in {city}', { city: city || '' });
    }
    return t('Picked for you in {city}', { city: city || '' });
  }, [highlights, city, t]);

  // Don't render anything if there's no city — better than an
  // empty box with a header above an empty row.
  if (!city) return null;

  // Show skeleton ONLY when we have a positive "generation in
  // flight" signal — i.e. an actual highlights doc exists with
  // status='generating'. We deliberately do NOT show a skeleton
  // on bare isLoading (the initial onSnapshot subscription), since
  // that produces a visible flash-then-vanish if the doc doesn't
  // exist yet for this user/city. Better UX: render nothing until
  // there's real content, and let the GuidePage backfill effect
  // request generation behind the scenes.
  if (highlights && highlights.status === 'generating') {
    return (
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Skeleton variant="text" width="50%" height={28} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'hidden' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                flexShrink: 0,
                width: 92,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Skeleton variant="circular" width={TILE_SIZE} height={TILE_SIZE} />
              <Skeleton variant="text" width={60} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (!highlights || highlights.status !== 'ready' || items.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {heading}
          </Typography>
          {totalCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {discoveredCount} / {totalCount}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            overflowX: 'auto',
            // Hide scrollbars — this is a fling-scroll, not a desktop
            // scrollbar list.
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            // Edge-padding so the first/last tile don't bump the
            // viewport edges
            px: 0.25,
            py: 0.5,
            // Snap-x for tactile fling stops
            scrollSnapType: 'x mandatory',
            '& > *': { scrollSnapAlign: 'start' },
          }}
        >
          {items.map((item) => (
            <HighlightTile
              key={item.id}
              item={item}
              onClick={() => setSelected(item)}
            />
          ))}
        </Box>
      </Box>
      <HighlightDetailSheet
        item={selected}
        cityKey={highlights?.city_key || null}
        onClose={() => setSelected(null)}
        onAskAbout={onAskAbout}
      />
    </>
  );
};
